import { useState, useRef, useCallback, useEffect } from "react";
import { auth, db, storage } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadString, getDownloadURL } from "firebase/storage";

const FUNCTION_URL = "https://analyzeskin-PLACEHOLDER-uc.a.run.app";
const STRIPE_PK = "pk_live_51QPWGVJHSi6e75OQVyILQMO4ejeGPvihz3364MWIUM3n7R2maILJI6LGqj8F7tHYUGc0MV7nuAeT3RGUHnF9kGP008SXDHKT6";

const PLANS = {
  basic: {
    id: "basic",
    name: "SkinReport",
    price: 299,
    priceId: "price_1TK2K6JHSi6e75OQsG10pwO1",
    paymentLink: "https://buy.stripe.com/5kQ3cwfaofnPeVGgMOdAk00",
    analyses: 30,
    color: "#8A7D70",
    features: ["30 análisis por mes", "Foto del paciente", "Historial de pacientes", "Reporte por WhatsApp", "14 días gratis"],
  },
  pro: {
    id: "pro",
    name: "SkinReport Pro",
    price: 599,
    priceId: "price_1TK2MdJHSi6e75OQ70SsGr6e",
    paymentLink: "https://buy.stripe.com/bJe9AUbYc2B36pagMOdAk01",
    analyses: -1,
    color: "#B8955A",
    popular: true,
    features: ["Análisis ilimitados", "Foto del paciente", "Seguimiento fotográfico", "Logo de tu negocio", "Mi Panel de métricas", "14 días gratis"],
  },
  proplus: {
    id: "proplus",
    name: "SkinReport Pro+",
    price: 1199,
    priceId: "price_1TK2ONJHSi6e75OQGf9O9VXE",
    paymentLink: "https://buy.stripe.com/cNi4gAfaoejLeVG0NQdAk02",
    analyses: -1,
    color: "#C4856A",
    features: ["Todo lo de Pro", "Hasta 5 usuarios", "Exportar CSV", "Soporte prioritario", "Acceso anticipado a nuevas funciones", "14 días gratis"],
  },
};

const C = {
  dark: "#0D0B08",
  dark2: "#1A1410",
  gold: "#B8955A",
  goldLight: "#D4AF7A",
  rose: "#C4856A",
  muted: "#8A7D70",
  border: "#E8E0D4",
  borderDark: "rgba(184,149,90,0.15)",
  cream: "#FAF7F2",
  white: "#FFFFFF",
  green: "#25D366",
  success: "#4A7C5F",
};

const sevColor = { alta: "#C4856A", media: "#B8955A", baja: "#4A7C5F" };
const sevBg = { alta: "rgba(196,133,106,0.1)", media: "rgba(184,149,90,0.1)", baja: "rgba(74,124,95,0.1)" };

const s = {
  wrap: { fontFamily: "'Jost', system-ui, sans-serif", background: C.cream, minHeight: "100vh", color: C.dark },
  header: { background: C.dark, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, borderBottom: `1px solid ${C.borderDark}` },
  main: { maxWidth: 460, margin: "0 auto", padding: "24px 20px 80px" },
  card: { background: C.white, borderRadius: 14, padding: 20, marginBottom: 16, border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" },
  input: { border: `1px solid ${C.border}`, borderRadius: 8, padding: "11px 14px", fontSize: 13, color: C.dark, background: C.cream, outline: "none", width: "100%", boxSizing: "border-box", fontFamily: "'Jost', sans-serif" },
  btn: (bg, color, full) => ({ width: full ? "100%" : "auto", background: bg, color, border: "none", borderRadius: 8, padding: "13px 20px", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "'Jost', sans-serif", fontWeight: 300 }),
  label: { fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: C.muted, marginBottom: 6, display: "block", fontFamily: "'Jost', sans-serif" },
  sectionLabel: { fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.gold, marginBottom: 12, fontFamily: "'Jost', sans-serif" },
};

const GOOGLE_FONTS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=Jost:wght@200;300;400&display=swap');`;

// ═══════════════════════════════════════════════════════════════════════════
// AUTH SCREEN — Login + Registro
// ═══════════════════════════════════════════════════════════════════════════
function AuthScreen() {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try { await signInWithEmailAndPassword(auth, email, password); }
    catch { setError("Correo o contraseña incorrectos."); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    if (password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres."); setLoading(false); return; }
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      // Crear perfil con trial de 14 días
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 14);
      await setDoc(doc(db, "users", cred.user.uid), {
        name, email,
        plan: "trial",
        trialEnd: trialEnd.toISOString(),
        analysesThisMonth: 0,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      if (err.code === "auth/email-already-in-use") setError("Este correo ya está registrado.");
      else setError("Error al crear cuenta. Intenta de nuevo.");
    } finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess("Te enviamos un correo para restablecer tu contraseña.");
    } catch { setError("No encontramos ese correo."); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.dark, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, position: "relative", overflow: "hidden" }}>
      <style>{`
        ${GOOGLE_FONTS}
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:0.3; transform:scale(1); } 50% { opacity:0.6; transform:scale(1.04); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .sr-input { background: rgba(255,255,255,0.04) !important; border: 1px solid rgba(184,149,90,0.2) !important; color: #FAF7F2 !important; border-radius: 8px !important; padding: 13px 16px !important; font-size: 13px !important; width: 100% !important; box-sizing: border-box !important; outline: none !important; font-family: 'Jost', sans-serif !important; transition: border-color 0.3s !important; }
        .sr-input::placeholder { color: rgba(255,255,255,0.2) !important; }
        .sr-input:focus { border-color: rgba(184,149,90,0.5) !important; }
        .sr-btn-gold { width:100%; background: linear-gradient(135deg, #B8955A, #D4AF7A, #B8955A); color: #0D0B08; border:none; border-radius:8px; padding:14px; font-size:10px; letter-spacing:3px; text-transform:uppercase; cursor:pointer; font-family:'Jost',sans-serif; font-weight:400; transition: opacity 0.3s; }
        .sr-btn-gold:hover { opacity:0.9; }
        .sr-btn-gold:disabled { opacity:0.4; cursor:not-allowed; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
      `}</style>

      {/* Orbes */}
      <div style={{ position:"absolute", top:"-15%", left:"-10%", width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle, rgba(184,149,90,0.1) 0%, transparent 70%)", animation:"pulse 8s ease-in-out infinite", pointerEvents:"none" }} />
      <div style={{ position:"absolute", bottom:"-15%", right:"-10%", width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle, rgba(196,133,106,0.07) 0%, transparent 70%)", animation:"pulse 10s ease-in-out infinite 3s", pointerEvents:"none" }} />

      <div style={{ width:"100%", maxWidth:400, position:"relative", zIndex:1, animation:"fadeUp 0.7s ease-out" }}>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom: mode === "register" ? 32 : 48 }}>
          <div style={{ width:56, height:56, border:"1px solid rgba(184,149,90,0.35)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", background:"rgba(184,149,90,0.05)" }}>
            <span style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:22, color:C.gold, fontWeight:300 }}>S</span>
          </div>
          <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:28, fontWeight:300, color:"#FAF7F2", letterSpacing:5, textTransform:"uppercase" }}>SKINREPORT</div>
          <div style={{ fontFamily:"'Jost', sans-serif", fontSize:8, fontWeight:200, color:C.gold, letterSpacing:5, textTransform:"uppercase", marginTop:5 }}>AI SKIN DIAGNOSTICS</div>
          <div style={{ width:32, height:1, background:`linear-gradient(to right, transparent, ${C.gold}, transparent)`, margin:"12px auto 0" }} />
        </div>

        {/* Card */}
        <div style={{ background:"rgba(255,255,255,0.03)", backdropFilter:"blur(20px)", border:"1px solid rgba(184,149,90,0.12)", borderRadius:16, padding:"32px 28px" }}>

          {mode === "login" && (
            <>
              <div style={{ textAlign:"center", marginBottom:24 }}>
                <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, color:"#FAF7F2", fontWeight:300, fontStyle:"italic" }}>Bienvenido de vuelta</div>
              </div>
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"rgba(184,149,90,0.6)", marginBottom:7, display:"block", fontFamily:"'Jost', sans-serif" }}>Correo</label>
                  <input className="sr-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" required />
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"rgba(184,149,90,0.6)", marginBottom:7, display:"block", fontFamily:"'Jost', sans-serif" }}>Contraseña</label>
                  <input className="sr-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
                </div>
                {error && <div style={{ background:"rgba(196,133,106,0.1)", border:"1px solid rgba(196,133,106,0.3)", borderRadius:8, padding:"10px 14px", fontSize:12, color:C.rose, marginBottom:14, fontFamily:"'Jost', sans-serif" }}>{error}</div>}
                <button className="sr-btn-gold" type="submit" disabled={loading}>{loading ? "Verificando..." : "Ingresar"}</button>
              </form>
              <div style={{ textAlign:"center", marginTop:20, display:"flex", flexDirection:"column", gap:8 }}>
                <button onClick={() => { setMode("register"); setError(""); }} style={{ background:"transparent", border:"none", color:"rgba(184,149,90,0.6)", fontSize:11, cursor:"pointer", fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>¿No tienes cuenta? Regístrate gratis</button>
                <button onClick={() => { setMode("forgot"); setError(""); }} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.2)", fontSize:10, cursor:"pointer", fontFamily:"'Jost', sans-serif" }}>Olvidé mi contraseña</button>
              </div>
            </>
          )}

          {mode === "register" && (
            <>
              <div style={{ textAlign:"center", marginBottom:24 }}>
                <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, color:"#FAF7F2", fontWeight:300, fontStyle:"italic" }}>Prueba 14 días gratis</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:4, fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>Sin tarjeta de crédito</div>
              </div>
              <form onSubmit={handleRegister}>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"rgba(184,149,90,0.6)", marginBottom:7, display:"block", fontFamily:"'Jost', sans-serif" }}>Nombre</label>
                  <input className="sr-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" required />
                </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"rgba(184,149,90,0.6)", marginBottom:7, display:"block", fontFamily:"'Jost', sans-serif" }}>Correo</label>
                  <input className="sr-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" required />
                </div>
                <div style={{ marginBottom:20 }}>
                  <label style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"rgba(184,149,90,0.6)", marginBottom:7, display:"block", fontFamily:"'Jost', sans-serif" }}>Contraseña</label>
                  <input className="sr-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" required />
                </div>
                {error && <div style={{ background:"rgba(196,133,106,0.1)", border:"1px solid rgba(196,133,106,0.3)", borderRadius:8, padding:"10px 14px", fontSize:12, color:C.rose, marginBottom:14, fontFamily:"'Jost', sans-serif" }}>{error}</div>}
                <button className="sr-btn-gold" type="submit" disabled={loading}>{loading ? "Creando cuenta..." : "Comenzar prueba gratis"}</button>
              </form>
              <button onClick={() => { setMode("login"); setError(""); }} style={{ background:"transparent", border:"none", color:"rgba(184,149,90,0.6)", fontSize:11, cursor:"pointer", fontFamily:"'Jost', sans-serif", letterSpacing:1, marginTop:16, width:"100%", textAlign:"center" }}>¿Ya tienes cuenta? Inicia sesión</button>
            </>
          )}

          {mode === "forgot" && (
            <>
              <div style={{ textAlign:"center", marginBottom:24 }}>
                <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, color:"#FAF7F2", fontWeight:300 }}>Restablecer contraseña</div>
              </div>
              <form onSubmit={handleForgot}>
                <div style={{ marginBottom:20 }}>
                  <label style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:"rgba(184,149,90,0.6)", marginBottom:7, display:"block", fontFamily:"'Jost', sans-serif" }}>Correo</label>
                  <input className="sr-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" required />
                </div>
                {error && <div style={{ background:"rgba(196,133,106,0.1)", border:"1px solid rgba(196,133,106,0.3)", borderRadius:8, padding:"10px 14px", fontSize:12, color:C.rose, marginBottom:14, fontFamily:"'Jost', sans-serif" }}>{error}</div>}
                {success && <div style={{ background:"rgba(74,124,95,0.1)", border:"1px solid rgba(74,124,95,0.3)", borderRadius:8, padding:"10px 14px", fontSize:12, color:C.success, marginBottom:14, fontFamily:"'Jost', sans-serif" }}>{success}</div>}
                <button className="sr-btn-gold" type="submit" disabled={loading}>{loading ? "Enviando..." : "Enviar correo"}</button>
              </form>
              <button onClick={() => { setMode("login"); setError(""); setSuccess(""); }} style={{ background:"transparent", border:"none", color:"rgba(184,149,90,0.6)", fontSize:11, cursor:"pointer", fontFamily:"'Jost', sans-serif", letterSpacing:1, marginTop:16, width:"100%", textAlign:"center" }}>← Volver</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PLANES — Pantalla de suscripción
// ═══════════════════════════════════════════════════════════════════════════
function PlansScreen({ user, userProfile }) {
  const isTrialActive = userProfile?.trialEnd && new Date(userProfile.trialEnd) > new Date();
  const daysLeft = isTrialActive ? Math.ceil((new Date(userProfile.trialEnd) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div style={{ minHeight:"100vh", background: C.dark, padding:"40px 20px" }}>
      <style>{`${GOOGLE_FONTS} @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }`}</style>
      <div style={{ maxWidth:460, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:28, color:"#FAF7F2", fontWeight:300, letterSpacing:3 }}>SKINREPORT</div>
          {isTrialActive && (
            <div style={{ marginTop:12, background:"rgba(184,149,90,0.1)", border:"1px solid rgba(184,149,90,0.3)", borderRadius:20, padding:"6px 16px", display:"inline-block" }}>
              <span style={{ fontSize:11, color:C.gold, fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>✨ {daysLeft} días de prueba restantes</span>
            </div>
          )}
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginTop:12, fontFamily:"'Jost', sans-serif", lineHeight:1.7 }}>
            Elige el plan que mejor se adapte a tu práctica profesional
          </div>
        </div>

        {Object.values(PLANS).map(plan => (
          <div key={plan.id} style={{ background: plan.popular ? "rgba(184,149,90,0.08)" : "rgba(255,255,255,0.03)", border: plan.popular ? `1px solid rgba(184,149,90,0.4)` : "1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"24px 22px", marginBottom:16, position:"relative", animation:"fadeUp 0.6s ease-out" }}>
            {plan.popular && (
              <div style={{ position:"absolute", top:-12, left:"50%", transform:"translateX(-50%)", background:`linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color:C.dark, fontSize:9, letterSpacing:2, textTransform:"uppercase", padding:"4px 16px", borderRadius:20, fontFamily:"'Jost', sans-serif", fontWeight:400, whiteSpace:"nowrap" }}>
                Más popular
              </div>
            )}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, color:"#FAF7F2", fontWeight:300 }}>{plan.name}</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", marginTop:3, fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>
                  {plan.analyses === -1 ? "Análisis ilimitados" : `${plan.analyses} análisis/mes`}
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:28, color:plan.popular ? C.gold : "#FAF7F2", fontWeight:300 }}>${plan.price}</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.3)", fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>MXN/mes</div>
              </div>
            </div>
            <div style={{ marginBottom:20 }}>
              {plan.features.map((f, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                  <div style={{ width:4, height:4, borderRadius:"50%", background: plan.popular ? C.gold : "rgba(255,255,255,0.3)", flexShrink:0 }} />
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)", fontFamily:"'Jost', sans-serif" }}>{f}</div>
                </div>
              ))}
            </div>
            <button
              onClick={() => window.open(plan.paymentLink, "_blank")}
              style={{ width:"100%", background: plan.popular ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : "rgba(255,255,255,0.06)", color: plan.popular ? C.dark : "#FAF7F2", border: plan.popular ? "none" : "1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"13px", fontSize:10, letterSpacing:2, textTransform:"uppercase", cursor:"pointer", fontFamily:"'Jost', sans-serif", fontWeight: plan.popular ? 400 : 300 }}
            >
              {isTrialActive ? "Activar plan" : "Comenzar 14 días gratis"}
            </button>
          </div>
        ))}

        <button onClick={() => signOut(auth)} style={{ width:"100%", background:"transparent", border:"none", color:"rgba(255,255,255,0.2)", fontSize:10, cursor:"pointer", fontFamily:"'Jost', sans-serif", marginTop:8, padding:"10px" }}>
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REPORTE VIEW
// ═══════════════════════════════════════════════════════════════════════════
function ReportView({ analysis, allAnalyses, onBack, onShare, userProfile }) {
  const r = analysis.report;
  const samePatient = (allAnalyses || []).filter(a => a.id !== analysis.id && a.patientName?.toLowerCase() === analysis.patientName?.toLowerCase()).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  const prev = samePatient[0] || null;
  const scoreDiff = prev ? (r.skinScore || 0) - (prev.report?.skinScore || 0) : null;
  const canSeeFollowUp = ["pro", "proplus", "trial"].includes(userProfile?.plan);

  return (
    <div>
      <button onClick={onBack} style={{ background:"transparent", border:"none", color:C.gold, fontSize:12, cursor:"pointer", marginBottom:16, display:"flex", alignItems:"center", gap:6, fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>← Volver</button>

      {/* Seguimiento fotográfico */}
      {canSeeFollowUp && analysis.photoURL && prev?.photoURL && (
        <div style={{ ...s.card, marginBottom:16 }}>
          <div style={s.sectionLabel}>Seguimiento fotográfico</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div>
              <img src={prev.photoURL} alt="anterior" style={{ width:"100%", aspectRatio:"1", objectFit:"cover", borderRadius:10 }} />
              <div style={{ textAlign:"center", marginTop:6 }}>
                <div style={{ fontSize:10, color:C.muted, fontFamily:"'Jost', sans-serif" }}>Sesión anterior</div>
                <div style={{ fontSize:20, color:C.muted, fontFamily:"'Cormorant Garamond', serif", fontWeight:300, marginTop:2 }}>{prev.report?.skinScore}</div>
              </div>
            </div>
            <div>
              <img src={analysis.photoURL} alt="actual" style={{ width:"100%", aspectRatio:"1", objectFit:"cover", borderRadius:10, border:`2px solid ${C.gold}` }} />
              <div style={{ textAlign:"center", marginTop:6 }}>
                <div style={{ fontSize:10, color:C.gold, fontFamily:"'Jost', sans-serif" }}>Sesión actual</div>
                <div style={{ fontSize:20, color:C.gold, fontFamily:"'Cormorant Garamond', serif", fontWeight:300, marginTop:2 }}>{r.skinScore}</div>
              </div>
            </div>
          </div>
          {scoreDiff !== null && (
            <div style={{ marginTop:12, padding:"10px 14px", background:C.cream, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontSize:11, color:C.muted, fontFamily:"'Jost', sans-serif" }}>Evolución del Skin Score</div>
              <div style={{ fontSize:13, fontWeight:500, color: scoreDiff >= 0 ? C.success : C.rose, fontFamily:"'Jost', sans-serif" }}>{scoreDiff >= 0 ? "▲" : "▼"} {Math.abs(scoreDiff)} pts</div>
            </div>
          )}
        </div>
      )}

      {analysis.photoURL && !prev?.photoURL && (
        <img src={analysis.photoURL} alt="paciente" style={{ width:"100%", maxHeight:240, objectFit:"cover", borderRadius:14, display:"block", marginBottom:16 }} />
      )}

      {/* Header reporte */}
      <div style={{ background:C.dark, borderRadius:"14px 14px 0 0", padding:"18px 20px" }}>
        {analysis.logoURL && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, paddingBottom:14, borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
            <img src={analysis.logoURL} alt="logo" style={{ height:32, maxWidth:120, objectFit:"contain" }} />
            <div style={{ fontSize:8, color:"rgba(255,255,255,0.25)", letterSpacing:1.5, textTransform:"uppercase", fontFamily:"'Jost', sans-serif" }}>Powered by SkinReport</div>
          </div>
        )}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:8, letterSpacing:3, textTransform:"uppercase", color:C.gold, marginBottom:4, fontFamily:"'Jost', sans-serif" }}>Diagnóstico Cutáneo</div>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:22, color:C.white, fontWeight:300 }}>{analysis.patientName}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginTop:2, fontFamily:"'Jost', sans-serif" }}>
              {analysis.businessName || ""} · {analysis.createdAt?.seconds ? new Date(analysis.createdAt.seconds * 1000).toLocaleDateString("es-MX") : ""}
            </div>
          </div>
          <div style={{ textAlign:"center", background:"rgba(184,149,90,0.12)", border:`1px solid rgba(184,149,90,0.3)`, borderRadius:12, padding:"10px 16px" }}>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:32, color:C.gold, lineHeight:1 }}>{r.skinScore}</div>
            <div style={{ fontSize:8, color:C.goldLight, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Jost', sans-serif", marginTop:2 }}>Skin Score</div>
          </div>
        </div>
      </div>

      <div style={{ background:C.white, borderRadius:"0 0 14px 14px", border:`1px solid ${C.border}`, borderTop:"none", marginBottom:16, overflow:"hidden" }}>

        {/* Tipo de piel */}
        {r.skinType && (
          <div style={{ padding:"14px 20px", borderBottom:`1px solid ${C.border}`, background:"rgba(184,149,90,0.03)", display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:C.muted, fontFamily:"'Jost', sans-serif" }}>Tipo de piel detectado</div>
            <div style={{ background:C.dark, color:C.gold, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", padding:"4px 12px", borderRadius:20, fontFamily:"'Jost', sans-serif" }}>{r.skinType}</div>
          </div>
        )}

        {/* Hallazgos */}
        <div style={{ padding:"18px 20px", borderBottom:`1px solid ${C.border}` }}>
          <div style={s.sectionLabel}>Hallazgos clínicos</div>
          {(r.findings || []).map((f, i) => {
            const sev = (f.severity || "media").toLowerCase();
            return (
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"8px 0", borderBottom: i < r.findings.length - 1 ? `1px solid rgba(232,224,212,0.5)` : "none" }}>
                <div style={{ width:7, height:7, borderRadius:"50%", flexShrink:0, marginTop:5, background:sevColor[sev] }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:400, marginBottom:2, fontFamily:"'Jost', sans-serif" }}>{f.name}</div>
                  <div style={{ fontSize:11, color:C.muted, lineHeight:1.6, fontFamily:"'Jost', sans-serif" }}>{f.description}</div>
                </div>
                <div style={{ flexShrink:0, fontSize:8, letterSpacing:1, textTransform:"uppercase", padding:"3px 8px", borderRadius:20, background:sevBg[sev], color:sevColor[sev], fontFamily:"'Jost', sans-serif" }}>{sev}</div>
              </div>
            );
          })}
        </div>

        {/* Protocolo */}
        <div style={{ padding:"18px 20px", borderBottom:`1px solid ${C.border}` }}>
          <div style={s.sectionLabel}>Protocolo recomendado</div>
          <div style={{ background:C.dark, borderRadius:12, padding:"16px 18px", marginBottom:14 }}>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:19, color:C.white, fontWeight:300, marginBottom:6 }}>{r.protocol?.name}</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", fontFamily:"'Jost', sans-serif", lineHeight:1.6 }}>{r.protocol?.description}</div>
            <div style={{ display:"flex", gap:16, marginTop:10 }}>
              {r.protocol?.sessions && <div style={{ fontSize:10, color:C.gold, fontFamily:"'Jost', sans-serif" }}>📅 {r.protocol.sessions}</div>}
              {r.protocol?.frequency && <div style={{ fontSize:10, color:C.gold, fontFamily:"'Jost', sans-serif" }}>🔄 {r.protocol.frequency}</div>}
            </div>
          </div>

          {/* Ingredientes activos */}
          {r.ingredients && r.ingredients.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:C.muted, marginBottom:8, fontFamily:"'Jost', sans-serif" }}>Ingredientes activos clave</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {r.ingredients.map((ing, i) => (
                  <div key={i} style={{ background:"rgba(184,149,90,0.08)", border:`1px solid rgba(184,149,90,0.2)`, borderRadius:20, padding:"4px 12px", fontSize:10, color:C.gold, fontFamily:"'Jost', sans-serif" }}>{ing}</div>
                ))}
              </div>
            </div>
          )}

          {/* Rutina de casa */}
          <div style={{ fontSize:9, letterSpacing:2, textTransform:"uppercase", color:C.muted, marginBottom:10, fontFamily:"'Jost', sans-serif" }}>Rutina para casa</div>
          {(r.homecare || []).map((item, i) => (
            <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"9px 12px", background:C.cream, borderRadius:8, marginBottom:8 }}>
              <div style={{ width:22, height:22, flexShrink:0, background:C.dark, color:C.gold, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontFamily:"'Jost', sans-serif", fontWeight:400 }}>{item.step}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontFamily:"'Jost', sans-serif", fontWeight:400 }}>{item.product}</div>
                <div style={{ fontSize:10, color:C.gold, marginTop:1, fontFamily:"'Jost', sans-serif" }}>{item.ingredient}</div>
                <div style={{ fontSize:11, color:C.muted, marginTop:2, fontFamily:"'Jost', sans-serif" }}>{item.how}</div>
              </div>
              <div style={{ fontSize:8, color:C.muted, background:C.border, borderRadius:4, padding:"2px 6px", fontFamily:"'Jost', sans-serif", flexShrink:0 }}>{item.when}</div>
            </div>
          ))}
        </div>

        {/* A evitar */}
        {r.avoid && r.avoid.length > 0 && (
          <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}` }}>
            <div style={s.sectionLabel}>Evitar</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {r.avoid.map((item, i) => (
                <div key={i} style={{ background:"rgba(196,133,106,0.08)", border:`1px solid rgba(196,133,106,0.2)`, borderRadius:20, padding:"4px 12px", fontSize:10, color:C.rose, fontFamily:"'Jost', sans-serif" }}>✗ {item}</div>
              ))}
            </div>
          </div>
        )}

        {/* Seguimiento y tip profesional */}
        <div style={{ padding:"16px 20px" }}>
          {r.followUp && (
            <div style={{ background:"rgba(184,149,90,0.06)", border:`1px solid rgba(184,149,90,0.15)`, borderRadius:8, padding:"10px 14px", fontSize:12, color:C.dark, marginBottom:10, fontFamily:"'Jost', sans-serif", lineHeight:1.6 }}>
              📌 {r.followUp}
            </div>
          )}
          {r.professionalTip && (
            <div style={{ background:"rgba(26,20,16,0.04)", border:`1px solid rgba(26,20,16,0.1)`, borderRadius:8, padding:"12px 14px", fontFamily:"'Jost', sans-serif" }}>
              <div style={{ fontSize:8, letterSpacing:2, textTransform:"uppercase", color:C.muted, marginBottom:6 }}>Nota profesional</div>
              <div style={{ fontSize:11, color:C.dark, lineHeight:1.7 }}>{r.professionalTip}</div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
        <button style={s.btn(C.green, C.white, true)} onClick={() => onShare(analysis)}>💬 WhatsApp</button>
        <button style={{ ...s.btn(C.cream, C.dark, true), border:`1px solid ${C.border}` }} onClick={() => window.print()}>📄 Imprimir</button>
      </div>
      <button style={{ ...s.btn("transparent", C.muted, true), border:`1px solid ${C.border}`, marginTop:4 }} onClick={onBack}>← Volver</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HISTORIAL
// ═══════════════════════════════════════════════════════════════════════════
function History({ user, userProfile, businessProfile }) {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      const q = query(collection(db, "analyses"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setAnalyses(data); setLoading(false);
    };
    fetchHistory();
  }, [user.uid]);

  const shareWhatsApp = (a) => {
    const r = a.report;
    const homecare = (r.homecare || []).map(p => `• Paso ${p.step}: ${p.product} (${p.ingredient})`).join("\n");
    const text = `*Diagnóstico Cutáneo Profesional*\n_Generado por ${businessProfile?.businessName || "SkinReport"}_\n\n👤 *${a.patientName}*\n📅 ${a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000).toLocaleDateString("es-MX") : ""}\n⭐ Skin Score: *${r.skinScore}/100*\n🔬 Tipo de piel: ${r.skinType || ""}\n\n*Protocolo recomendado:*\n${r.protocol?.name}\n${r.protocol?.sessions || ""}\n\n*Rutina para casa:*\n${homecare}\n\n📌 ${r.followUp || ""}\n\n_Powered by SkinReport AI · skinreport.mx_`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (loading) return <div style={{ textAlign:"center", padding:40, color:C.muted, fontSize:13, fontFamily:"'Jost', sans-serif" }}>Cargando historial...</div>;
  if (!analyses.length) return (
    <div style={{ textAlign:"center", padding:40 }}>
      <div style={{ fontSize:36, marginBottom:16 }}>📋</div>
      <div style={{ fontSize:16, fontFamily:"'Cormorant Garamond', serif", fontWeight:300, marginBottom:8 }}>Sin análisis aún</div>
      <div style={{ fontSize:12, color:C.muted, fontFamily:"'Jost', sans-serif" }}>Realiza tu primer análisis para verlo aquí.</div>
    </div>
  );

  if (selected) return <ReportView analysis={selected} allAnalyses={analyses} onBack={() => setSelected(null)} onShare={shareWhatsApp} userProfile={userProfile} />;

  return (
    <div>
      <div style={{ fontSize:9, letterSpacing:3, textTransform:"uppercase", color:C.gold, marginBottom:20, fontFamily:"'Jost', sans-serif" }}>Historial · {analyses.length} análisis</div>
      {analyses.map(a => (
        <div key={a.id} onClick={() => setSelected(a)} style={{ marginBottom:16, borderRadius:16, overflow:"hidden", cursor:"pointer", boxShadow:"0 4px 20px rgba(0,0,0,0.08)", border:`1px solid ${C.border}`, background:C.white, transition:"transform 0.2s, box-shadow 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 32px rgba(0,0,0,0.12)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,0.08)"; }}>
          {a.photoURL ? (
            <div style={{ position:"relative", height:160, overflow:"hidden" }}>
              <img src={a.photoURL} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
              <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top, rgba(13,11,8,0.92) 0%, rgba(13,11,8,0.3) 60%, transparent 100%)" }} />
              <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"14px 16px", display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
                <div>
                  <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:18, color:C.white, fontWeight:300 }}>{a.patientName}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.45)", fontFamily:"'Jost', sans-serif", marginTop:2 }}>{a.report?.protocol?.name}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:26, color:C.gold, lineHeight:1 }}>{a.report?.skinScore}</div>
                  <div style={{ fontSize:7, color:C.gold, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Jost', sans-serif" }}>score</div>
                </div>
              </div>
              <div style={{ position:"absolute", top:10, right:10, background:"rgba(0,0,0,0.45)", backdropFilter:"blur(8px)", borderRadius:20, padding:"3px 10px", fontSize:9, color:"rgba(255,255,255,0.6)", fontFamily:"'Jost', sans-serif" }}>
                {a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000).toLocaleDateString("es-MX") : ""}
              </div>
            </div>
          ) : (
            <div style={{ background:`linear-gradient(135deg, ${C.dark}, #2A1F14)`, height:90, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px" }}>
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:18, color:C.white }}>{a.patientName}</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", fontFamily:"'Jost', sans-serif", marginTop:2 }}>{a.report?.protocol?.name}</div>
              </div>
              <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:26, color:C.gold }}>{a.report?.skinScore}</div>
            </div>
          )}
          <div style={{ padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:10, color:C.muted, fontFamily:"'Jost', sans-serif" }}>
              {a.report?.findings?.slice(0,2).map(f => f.name).join(" · ")}
            </div>
            <div style={{ fontSize:10, color:C.gold, fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>Ver →</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ANALIZADOR
// ═══════════════════════════════════════════════════════════════════════════
function Analyzer({ user, userProfile, businessProfile }) {
  const [step, setStep] = useState("upload");
  const [imageBase64, setImageBase64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [patientAge, setPatientAge] = useState("");
  const [skinType, setSkinType] = useState("");
  const [mainConcern, setMainConcern] = useState("");
  const [report, setReport] = useState(null);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [loadingStep, setLoadingStep] = useState(0);
  const fileRef = useRef();
  const cameraRef = useRef();

  const canAnalyze = () => {
    if (!userProfile) return false;
    const plan = userProfile.plan;
    if (plan === "trial") return new Date(userProfile.trialEnd) > new Date();
    if (plan === "pro" || plan === "proplus") return true;
    if (plan === "basic") return (userProfile.analysesThisMonth || 0) < 30;
    return false;
  };

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX = 800; let w = img.width, h = img.height;
        if (w > h) { if (w > MAX) { h = h * MAX / w; w = MAX; } } else { if (h > MAX) { w = w * MAX / h; h = MAX; } }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL("image/jpeg", 0.8);
        setImagePreview(compressed); setImageBase64(compressed.split(",")[1]); setStep("form");
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, []);

  const analyze = async () => {
    if (!imageBase64 || !canAnalyze()) return;
    setStep("loading"); setLoadingStep(1);
    setTimeout(() => setLoadingStep(2), 2500);
    setTimeout(() => setLoadingStep(3), 5000);
    try {
      const res = await fetch(FUNCTION_URL, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ imageBase64, imageType:"image/jpeg", patientName, patientAge, skinType, mainConcern }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Error en el análisis");

      let photoURL = null;
      try {
        const photoRef = ref(storage, `analyses/${user.uid}/${Date.now()}.jpg`);
        await uploadString(photoRef, imageBase64, "base64", { contentType:"image/jpeg" });
        photoURL = await getDownloadURL(photoRef);
      } catch (e) { console.warn("No se pudo guardar la foto:", e.message); }

      const docRef = await addDoc(collection(db, "analyses"), {
        userId: user.uid,
        businessName: businessProfile?.businessName || "",
        logoURL: businessProfile?.logoURL || null,
        patientName: patientName || "Paciente",
        patientAge, skinType, mainConcern,
        report: data.report,
        photoURL: photoURL || null,
        createdAt: serverTimestamp(),
      });

      // Actualizar contador del mes
      if (userProfile?.plan === "basic") {
        await updateDoc(doc(db, "users", user.uid), { analysesThisMonth: (userProfile.analysesThisMonth || 0) + 1 });
      }

      setReport(data.report);
      setCurrentAnalysis({ id:docRef.id, userId:user.uid, businessName:businessProfile?.businessName||"", logoURL:businessProfile?.logoURL||null, patientName:patientName||"Paciente", patientAge, skinType, mainConcern, report:data.report, photoURL:photoURL||null });
      setStep("results");
    } catch (e) { setError(e.message); setStep("error"); }
  };

  const shareWhatsApp = () => {
    if (!report) return;
    const homecare = (report.homecare || []).map(p => `• Paso ${p.step}: ${p.product} (${p.ingredient})`).join("\n");
    const text = `*Diagnóstico Cutáneo Profesional*\n_Generado por ${businessProfile?.businessName || "SkinReport"}_\n\n👤 *${patientName || "Paciente"}*\n📅 ${new Date().toLocaleDateString("es-MX", { day:"numeric", month:"long", year:"numeric" })}\n⭐ Skin Score: *${report.skinScore}/100*\n🔬 Tipo de piel: ${report.skinType || ""}\n\n*Protocolo:* ${report.protocol?.name}\n${report.protocol?.sessions || ""}\n\n*Rutina para casa:*\n${homecare}\n\n📌 ${report.followUp || ""}\n\n_Powered by SkinReport AI · skinreport.mx_`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const reset = () => {
    setStep("upload"); setImageBase64(null); setImagePreview(null); setReport(null); setCurrentAnalysis(null); setError("");
    setPatientName(""); setPatientAge(""); setSkinType(""); setMainConcern(""); setLoadingStep(0);
  };

  if (!canAnalyze()) return (
    <div style={{ textAlign:"center", padding:"40px 20px" }}>
      <div style={{ fontSize:36, marginBottom:16 }}>⏰</div>
      <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:22, fontWeight:300, marginBottom:8 }}>Prueba finalizada</div>
      <div style={{ fontSize:13, color:C.muted, fontFamily:"'Jost', sans-serif", marginBottom:24, lineHeight:1.7 }}>Tu período de prueba ha terminado. Activa un plan para continuar usando SkinReport.</div>
      <button style={s.btn(C.dark, C.white, true)} onClick={() => window.location.href = "https://skinreport.mx/#planes"}>Ver planes</button>
    </div>
  );

  if (step === "upload") return (
    <div>
      <div style={{ textAlign:"center", padding:"28px 0 20px" }}>
        <div style={{ fontSize:9, letterSpacing:3, textTransform:"uppercase", color:C.gold, marginBottom:10, fontFamily:"'Jost', sans-serif" }}>Nuevo diagnóstico</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:30, fontWeight:300, lineHeight:1.2 }}>Fotografía del<br /><em style={{ color:C.rose, fontStyle:"italic" }}>paciente</em></h2>
        <p style={{ fontSize:12, color:C.muted, marginTop:10, lineHeight:1.8, fontFamily:"'Jost', sans-serif" }}>Rostro sin maquillaje · Iluminación natural · Vista frontal</p>
      </div>
      <div style={{ background:C.white, border:`1.5px dashed ${C.border}`, borderRadius:16, padding:"36px 24px", textAlign:"center", marginBottom:16, boxShadow:"0 2px 12px rgba(0,0,0,0.04)" }}>
        <div style={{ width:54, height:54, background:"rgba(184,149,90,0.08)", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", fontSize:22 }}>📸</div>
        <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, fontWeight:300, marginBottom:6 }}>Cargar fotografía</div>
        <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:14 }}>
          <button style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 18px", fontSize:10, letterSpacing:1.5, textTransform:"uppercase", color:C.muted, cursor:"pointer", fontFamily:"'Jost', sans-serif" }} onClick={() => cameraRef.current?.click()}>📷 Cámara</button>
          <button style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, padding:"9px 18px", fontSize:10, letterSpacing:1.5, textTransform:"uppercase", color:C.muted, cursor:"pointer", fontFamily:"'Jost', sans-serif" }} onClick={() => fileRef.current?.click()}>🖼 Galería</button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])} />
      <input ref={cameraRef} type="file" accept="image/*" capture="user" style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])} />
    </div>
  );

  if (step === "form") return (
    <div>
      <img src={imagePreview} alt="preview" style={{ width:"100%", maxHeight:260, objectFit:"cover", borderRadius:14, display:"block", marginBottom:16 }} />
      <div style={s.card}>
        <div style={s.sectionLabel}>Datos del paciente</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
          <div><label style={s.label}>Nombre</label><input style={s.input} value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="Nombre" /></div>
          <div><label style={s.label}>Edad</label><input style={s.input} type="number" value={patientAge} onChange={e => setPatientAge(e.target.value)} placeholder="Ej: 35" min="16" max="90" /></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div>
            <label style={s.label}>Tipo de piel</label>
            <select style={s.input} value={skinType} onChange={e => setSkinType(e.target.value)}>
              <option value="">Seleccionar</option>
              {["Normal","Seca","Grasa","Mixta","Sensible","Madura"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Preocupación</label>
            <select style={s.input} value={mainConcern} onChange={e => setMainConcern(e.target.value)}>
              <option value="">Seleccionar</option>
              {["Manchas","Arrugas","Acné","Hidratación","Flacidez","Rojeces","Poros","Opacidad","Sensibilidad"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
      </div>
      <button style={s.btn(C.dark, C.white, true)} onClick={analyze}>🔬 Generar diagnóstico</button>
      <button style={{ ...s.btn("transparent", C.muted, true), border:`1px solid ${C.border}`, marginTop:8 }} onClick={reset}>← Cambiar foto</button>
    </div>
  );

  if (step === "loading") return (
    <div style={{ textAlign:"center", padding:"60px 20px" }}>
      <div style={{ width:48, height:48, border:`2px solid ${C.border}`, borderTopColor:C.gold, borderRadius:"50%", margin:"0 auto 24px", animation:"spin 1s linear infinite" }} />
      <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:24, fontWeight:300, marginBottom:6 }}>Analizando...</div>
      <div style={{ fontSize:11, color:C.muted, marginBottom:28, fontFamily:"'Jost', sans-serif", letterSpacing:1 }}>La IA está evaluando las condiciones de la piel</div>
      {["Detectando condiciones cutáneas", "Evaluando tipo de piel e ingredientes", "Generando protocolo personalizado"].map((txt, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"5px 0", fontSize:11, color: loadingStep > i ? C.gold : C.muted, fontFamily:"'Jost', sans-serif", transition:"color 0.5s" }}>
          <div style={{ width:5, height:5, borderRadius:"50%", background: loadingStep > i ? C.gold : C.border, transition:"background 0.5s" }} />{txt}
        </div>
      ))}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (step === "error") return (
    <div style={{ textAlign:"center", padding:"40px 20px" }}>
      <div style={{ fontSize:40, marginBottom:16 }}>⚠️</div>
      <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:22, fontWeight:300, marginBottom:8 }}>Error en el análisis</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:24, fontFamily:"'Jost', sans-serif" }}>{error}</div>
      <button style={s.btn(C.dark, C.white, false)} onClick={reset}>Intentar de nuevo</button>
    </div>
  );

  if (step === "results" && report && currentAnalysis) return (
    <ReportView analysis={currentAnalysis} allAnalyses={[]} onBack={reset} onShare={shareWhatsApp} userProfile={userProfile} />
  );

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// MI NEGOCIO (perfil)
// ═══════════════════════════════════════════════════════════════════════════
function BusinessProfile({ user, profile, onSave, userProfile }) {
  const [businessName, setBusinessName] = useState(profile?.businessName || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [city, setCity] = useState(profile?.city || "");
  const [logoURL, setLogoURL] = useState(profile?.logoURL || null);
  const [saved, setSaved] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef();

  const canUseLogo = ["pro", "proplus", "trial"].includes(userProfile?.plan);

  const handleLogo = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const MAX = 400; let w = img.width, h = img.height;
          if (w > h) { if (w > MAX) { h = h * MAX / w; w = MAX; } } else { if (h > MAX) { w = w * MAX / h; h = MAX; } }
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const base64 = canvas.toDataURL("image/jpeg", 0.9).split(",")[1];
          const logoRef2 = ref(storage, `logos/${user.uid}/logo.jpg`);
          await uploadString(logoRef2, base64, "base64", { contentType:"image/jpeg" });
          const url = await getDownloadURL(logoRef2);
          setLogoURL(url); setUploadingLogo(false);
        };
        img.src = e.target.result;
      } catch (err) { console.error(err); setUploadingLogo(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    await setDoc(doc(db, "businessProfiles", user.uid), { userId:user.uid, businessName, phone, city, logoURL:logoURL||null, updatedAt:serverTimestamp() });
    setSaved(true); onSave({ businessName, phone, city, logoURL:logoURL||null }); setTimeout(() => setSaved(false), 2000);
  };

  const planName = PLANS[userProfile?.plan]?.name || (userProfile?.plan === "trial" ? "Trial Pro" : "Sin plan");
  const isTrialActive = userProfile?.plan === "trial" && userProfile?.trialEnd && new Date(userProfile.trialEnd) > new Date();
  const daysLeft = isTrialActive ? Math.ceil((new Date(userProfile.trialEnd) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div>
      {/* Plan actual */}
      <div style={{ background:C.dark, borderRadius:14, padding:"18px 20px", marginBottom:16 }}>
        <div style={{ fontSize:8, letterSpacing:3, textTransform:"uppercase", color:C.gold, marginBottom:6, fontFamily:"'Jost', sans-serif" }}>Plan activo</div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, color:C.white, fontWeight:300 }}>{planName}</div>
          {isTrialActive && <div style={{ fontSize:10, color:C.gold, fontFamily:"'Jost', sans-serif" }}>⏳ {daysLeft} días restantes</div>}
        </div>
        {userProfile?.plan === "basic" && (
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:6, fontFamily:"'Jost', sans-serif" }}>
            {userProfile.analysesThisMonth || 0}/30 análisis este mes
          </div>
        )}
      </div>

      {/* Perfil del negocio */}
      <div style={s.card}>
        <div style={s.sectionLabel}>Mi negocio</div>

        {canUseLogo && (
          <div style={{ marginBottom:16 }}>
            <label style={s.label}>Logo</label>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:68, height:68, background:C.cream, border:`1px solid ${C.border}`, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", flexShrink:0 }}>
                {logoURL ? <img src={logoURL} alt="logo" style={{ width:"100%", height:"100%", objectFit:"contain" }} /> : <span style={{ fontSize:22 }}>🏪</span>}
              </div>
              <div style={{ flex:1 }}>
                <button onClick={() => logoRef.current?.click()} style={{ ...s.btn(C.cream, C.dark, true), border:`1px solid ${C.border}`, fontSize:10, padding:"9px" }} disabled={uploadingLogo}>
                  {uploadingLogo ? "Subiendo..." : logoURL ? "Cambiar logo" : "Subir logo"}
                </button>
                <div style={{ fontSize:9, color:C.muted, marginTop:5, fontFamily:"'Jost', sans-serif" }}>JPG o PNG · Fondo blanco recomendado</div>
              </div>
            </div>
            <input ref={logoRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e => handleLogo(e.target.files[0])} />
          </div>
        )}

        <div style={{ marginBottom:12 }}>
          <label style={s.label}>Nombre del negocio</label>
          <input style={s.input} value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Ej: Clínica Estética Premium" />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
          <div><label style={s.label}>WhatsApp</label><input style={s.input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="+52 55 1234 5678" /></div>
          <div><label style={s.label}>Ciudad</label><input style={s.input} value={city} onChange={e => setCity(e.target.value)} placeholder="Ciudad de México" /></div>
        </div>
        <button style={s.btn(saved ? C.success : C.dark, C.white, true)} onClick={handleSave}>{saved ? "✓ Guardado" : "Guardar"}</button>
      </div>

      <button onClick={() => signOut(auth)} style={{ ...s.btn("transparent", C.muted, true), border:`1px solid ${C.border}` }}>Cerrar sesión</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MI PANEL (métricas del negocio)
// ═══════════════════════════════════════════════════════════════════════════
function MyPanel({ user, userProfile, businessProfile }) {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  const canSeePanel = ["pro", "proplus", "trial"].includes(userProfile?.plan);

  useEffect(() => {
    if (!canSeePanel) { setLoading(false); return; }
    const fetchData = async () => {
      const q = query(collection(db, "analyses"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAnalyses(data); setLoading(false);
    };
    fetchData();
  }, [user.uid, canSeePanel]);

  if (!canSeePanel) return (
    <div style={{ textAlign:"center", padding:"40px 20px" }}>
      <div style={{ fontSize:36, marginBottom:16 }}>📊</div>
      <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:22, fontWeight:300, marginBottom:8 }}>Disponible en Pro</div>
      <div style={{ fontSize:12, color:C.muted, fontFamily:"'Jost', sans-serif", marginBottom:24, lineHeight:1.7 }}>El panel de métricas está disponible en los planes SkinReport Pro y Pro+.</div>
      <button style={s.btn(C.dark, C.white, false)} onClick={() => window.location.href = "https://skinreport.mx/#planes"}>Ver planes</button>
    </div>
  );

  const productStats = analyses.reduce((acc, a) => { (a.report?.ingredients || []).forEach(p => { if (!acc[p]) acc[p] = 0; acc[p]++; }); return acc; }, {});
  const pathStats = analyses.reduce((acc, a) => { (a.report?.findings || []).forEach(f => { if (!acc[f.name]) acc[f.name] = 0; acc[f.name]++; }); return acc; }, {});
  const topIngredients = Object.entries(productStats).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const topPaths = Object.entries(pathStats).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const avgScore = analyses.length ? Math.round(analyses.reduce((s, a) => s + (a.report?.skinScore || 0), 0) / analyses.length) : 0;
  const uniquePatients = new Set(analyses.map(a => a.patientName?.toLowerCase())).size;

  if (loading) return <div style={{ textAlign:"center", padding:40, color:C.muted, fontSize:13, fontFamily:"'Jost', sans-serif" }}>Cargando...</div>;

  if (!analyses.length) return (
    <div style={{ textAlign:"center", padding:40 }}>
      <div style={{ fontSize:36, marginBottom:16 }}>📊</div>
      <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:20, fontWeight:300, marginBottom:8 }}>Aún no hay datos</div>
      <div style={{ fontSize:12, color:C.muted, fontFamily:"'Jost', sans-serif" }}>Realiza análisis para ver tus métricas.</div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize:9, letterSpacing:3, textTransform:"uppercase", color:C.gold, marginBottom:20, fontFamily:"'Jost', sans-serif" }}>
        Mi Panel · {businessProfile?.businessName || "Mi Negocio"}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:20 }}>
        {[{ label:"Análisis", value:analyses.length }, { label:"Score prom", value:avgScore }, { label:"Pacientes", value:uniquePatients }].map((m, i) => (
          <div key={i} style={{ background:C.white, borderRadius:12, padding:"14px 12px", border:`1px solid ${C.border}`, textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:28, color:C.gold, fontWeight:300 }}>{m.value}</div>
            <div style={{ fontSize:8, color:C.muted, letterSpacing:1.5, textTransform:"uppercase", marginTop:4, fontFamily:"'Jost', sans-serif" }}>{m.label}</div>
          </div>
        ))}
      </div>
      <div style={s.card}>
        <div style={s.sectionLabel}>Ingredientes más recetados</div>
        {topIngredients.map(([name, count], i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <div style={{ fontSize:10, color:C.muted, minWidth:16, textAlign:"center", fontFamily:"'Jost', sans-serif" }}>{i+1}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, fontFamily:"'Jost', sans-serif", marginBottom:3 }}>{name}</div>
              <div style={{ background:C.cream, borderRadius:4, height:4, overflow:"hidden" }}><div style={{ height:"100%", background:C.gold, width:`${(count/(topIngredients[0]?.[1]||1))*100}%`, borderRadius:4 }} /></div>
            </div>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:18, color:C.gold, fontWeight:300 }}>{count}</div>
          </div>
        ))}
      </div>
      <div style={s.card}>
        <div style={s.sectionLabel}>Condiciones más frecuentes</div>
        {topPaths.map(([name, count], i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <div style={{ fontSize:10, color:C.muted, minWidth:16, textAlign:"center", fontFamily:"'Jost', sans-serif" }}>{i+1}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, fontFamily:"'Jost', sans-serif", marginBottom:3 }}>{name}</div>
              <div style={{ background:C.cream, borderRadius:4, height:4, overflow:"hidden" }}><div style={{ height:"100%", background:C.rose, width:`${(count/(topPaths[0]?.[1]||1))*100}%`, borderRadius:4 }} /></div>
            </div>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:18, color:C.rose, fontWeight:300 }}>{count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// APP PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [tab, setTab] = useState("analyzer");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Cargar perfil del usuario
        const userDoc = await getDoc(doc(db, "users", u.uid));
        if (userDoc.exists()) setUserProfile(userDoc.data());

        // Cargar perfil del negocio
        const bizDoc = await getDoc(doc(db, "businessProfiles", u.uid));
        if (bizDoc.exists()) setBusinessProfile(bizDoc.data());
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  if (authLoading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:C.dark }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:36, height:36, border:`1px solid rgba(184,149,90,0.3)`, borderTopColor:C.gold, borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 16px" }} />
        <div style={{ fontFamily:"'Jost', sans-serif", fontSize:9, letterSpacing:3, color:"rgba(184,149,90,0.5)", textTransform:"uppercase" }}>Cargando</div>
      </div>
      <style>{`${GOOGLE_FONTS} @keyframes spin { to { transform: rotate(360deg); } } * { box-sizing: border-box; margin: 0; padding: 0; }`}</style>
    </div>
  );

  if (!user) return <AuthScreen />;

  // Verificar si necesita plan
  const needsPlan = !userProfile || (userProfile.plan === "trial" && new Date(userProfile.trialEnd) < new Date());
  if (needsPlan && userProfile) return <PlansScreen user={user} userProfile={userProfile} />;

  const tabs = [
    { id:"analyzer", label:"Análisis", icon:"🔬" },
    { id:"history", label:"Historial", icon:"📋" },
    { id:"mipanel", label:"Mi Panel", icon:"📊" },
    { id:"profile", label:"Perfil", icon:"⚙️" },
  ];

  return (
    <div style={s.wrap}>
      <style>{`${GOOGLE_FONTS} @keyframes spin { to { transform: rotate(360deg); } } * { box-sizing: border-box; margin: 0; padding: 0; }`}</style>

      {/* Header */}
      <div style={s.header}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:30, height:30, border:`1px solid rgba(184,149,90,0.35)`, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:13, color:C.gold }}>S</span>
          </div>
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond', serif", color:C.white, fontSize:15, fontWeight:300, letterSpacing:3, textTransform:"uppercase" }}>SKINREPORT</div>
            <div style={{ fontSize:7, color:C.gold, letterSpacing:2.5, textTransform:"uppercase", fontFamily:"'Jost', sans-serif", fontWeight:200 }}>AI Diagnostics</div>
          </div>
        </div>
        {userProfile?.plan === "trial" && (
          <div style={{ fontSize:9, color:C.gold, fontFamily:"'Jost', sans-serif", background:"rgba(184,149,90,0.1)", border:`1px solid rgba(184,149,90,0.2)`, borderRadius:20, padding:"4px 10px" }}>
            Trial · {Math.max(0, Math.ceil((new Date(userProfile.trialEnd) - new Date()) / (1000 * 60 * 60 * 24)))} días
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ background:C.dark, borderBottom:`1px solid ${C.borderDark}`, display:"flex" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex:1, background:"transparent", border:"none", borderBottom: tab===t.id ? `1px solid ${C.gold}` : "1px solid transparent", padding:"11px 4px", fontSize:8, letterSpacing:1.5, textTransform:"uppercase", color: tab===t.id ? C.gold : "rgba(255,255,255,0.25)", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:4, fontFamily:"'Jost', sans-serif", fontWeight:300 }}>
            <span style={{ fontSize:14 }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      <div style={s.main}>
        {tab === "analyzer" && <Analyzer user={user} userProfile={userProfile} businessProfile={businessProfile} />}
        {tab === "history" && <History user={user} userProfile={userProfile} businessProfile={businessProfile} />}
        {tab === "mipanel" && <MyPanel user={user} userProfile={userProfile} businessProfile={businessProfile} />}
        {tab === "profile" && <BusinessProfile user={user} profile={businessProfile} onSave={setBusinessProfile} userProfile={userProfile} />}
      </div>
    </div>
  );
}
